import { faro } from '@grafana/faro-web-sdk';
import * as Sentry from '@sentry/react';
import type { PropsWithChildren, ReactElement } from 'react';
import { Component, useCallback } from 'react';
import { IS_PRODUCTION } from '../../utils/envs';
import { captureError } from '../../web-tracing';
import { errorFallbackRenderer } from './errorFallbackRenderer';

interface ErrorBoundaryProps {
	onError?: (error: Error) => void;
	disableFallback?: boolean;
	fallback?: (error: {
		error: unknown;
		componentStack: string;
		eventId: string;
		resetError(): void;
	}) => ReactElement;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

class MockErrorBoundary extends Component<
	PropsWithChildren<ErrorBoundaryProps>,
	ErrorBoundaryState
> {
	static getDerivedStateFromError(error: Error) {
		return { hasError: true, error };
	}

	constructor(props: PropsWithChildren<ErrorBoundaryProps>) {
		super(props);

		this.state = {
			hasError: false,
			error: null,
		};
	}

	componentDidCatch(error: Error, info: React.ErrorInfo) {
		const { onError } = this.props;

		// Local testing - just to make sure we're capturing errors with the
		// ProdErrorBoundary too.
		captureError(error);

		// capture the error in Sentry
		Sentry.captureReactException(error, info);

		if (onError) {
			onError(error);
		}
	}

	render() {
		const { hasError, error } = this.state;
		const {
			children,
			disableFallback,
			fallback = errorFallbackRenderer,
		} = this.props;

		if (hasError && !!error && !disableFallback) {
			return fallback({
				error,
				componentStack: '',
				eventId: '',
				resetError: () => {},
			});
		}

		return children;
	}
}

function ProdErrorBoundary({
	children,
	onError,
	disableFallback,
	fallback = errorFallbackRenderer,
}: PropsWithChildren<ErrorBoundaryProps>) {
	const handleOnError = useCallback(
		(error: unknown) => {
			// make sure to capture the error in Grafana
			if (faro) {
				faro.api.pushError(error as Error);
			}

			if (onError) {
				onError(error as Error);
			}
		},
		[onError]
	);

	return (
		<Sentry.ErrorBoundary
			fallback={disableFallback ? undefined : fallback}
			onError={handleOnError}
		>
			{children}
		</Sentry.ErrorBoundary>
	);
}

export function ErrorBoundary(props: PropsWithChildren<ErrorBoundaryProps>) {
	return IS_PRODUCTION ? (
		<ProdErrorBoundary {...props} />
	) : (
		<MockErrorBoundary {...props} />
	);
}
